import React, { useState, useEffect } from 'react';
import './CadastrarVeiculo.css';
import { message } from 'antd';
import api from '../../../../services/api';
import API_CONFIG from '../../../../config/api.config';

const initialState = {
  descricao: '',
  prefeitura: '',
  secretaria: '',
  placa: '',
  chassi: '',
  tipo: '',
};

const tiposVeiculo = [
  { id: 'MOTO', nome: 'Moto' },
  { id: 'CARRO', nome: 'Carro' },
  { id: 'ONIBUS', nome: 'Ônibus' },
  { id: 'CAMINHAO', nome: 'Caminhão' },
  { id: 'MAQUINA', nome: 'Máquina' },
];

// Função para formatar a placa (antiga e Mercosul)
const formatPlaca = (value) => {
  if (!value) return '';
  let cleanedValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  
  // Limita o tamanho para não formatar placas muito longas que não se encaixam nos padrões
  if (cleanedValue.length > 7) {
      cleanedValue = cleanedValue.substring(0, 7);
  }

  // Formato Mercosul (AAA1A11) ou antiga (AAA1111)
  if (cleanedValue.length > 3) {
      // Tenta formatar como Mercosul
      const mercosulMatch = cleanedValue.match(/^([A-Z]{3})([0-9]{1})([A-Z]{1})([0-9]{2})$/);
      if (mercosulMatch) {
          return `${mercosulMatch[1]}${mercosulMatch[2]}${mercosulMatch[3]}${mercosulMatch[4]}`;
      }
      // Tenta formatar como antiga
      const antigaMatch = cleanedValue.match(/^([A-Z]{3})([0-9]{4})$/);
       if (antigaMatch) {
          return `${antigaMatch[1]}-${antigaMatch[2]}`;
      }

       // Formatação parcial enquanto digita (AAA-1 ou AAA1A)
      if (cleanedValue.length > 3) {
         // Verifica se o 4º caractere é número para o formato antigo
         if (/[0-9]/.test(cleanedValue[3])) {
            return cleanedValue.substring(0, 3) + '-' + cleanedValue.substring(3);
         } else { // Senão, tenta formato Mercosul
             return cleanedValue;
         }
      }
  }

  return cleanedValue;
};

// Função para formatar o chassi (apenas maiúsculas e limite opcional)
const formatChassi = (value) => {
    if (!value) return '';
    // Remove caracteres não alfanuméricos e converte para maiúsculas
    let cleanedValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    // O chassi padrão tem 17 caracteres
    return cleanedValue.substring(0, 17);
};

export default function CadastrarVeiculo() {
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);
  const [prefeituras, setPrefeituras] = useState([]);
  const [secretarias, setSecretarias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingPrefeituras, setLoadingPrefeituras] = useState(false);
  const [loadingSecretarias, setLoadingSecretarias] = useState(false);

  useEffect(() => {
    const handleMenuChange = (event) => {
      setIsMenuExpanded(!event.detail.isCollapsed);
    };
    window.addEventListener('menuStateChange', handleMenuChange);
    return () => window.removeEventListener('menuStateChange', handleMenuChange);
  }, []);

  // Carregar prefeituras ao montar o componente
  useEffect(() => {
    carregarPrefeituras();
  }, []);

  // Carregar secretarias quando a prefeitura for alterada
  useEffect(() => {
    if (form.prefeitura) {
      carregarSecretarias(form.prefeitura);
    } else {
      setSecretarias([]);
      setForm(prev => ({ ...prev, secretaria: '' }));
    }
  }, [form.prefeitura]);

  const carregarPrefeituras = async () => {
    try {
      setLoadingPrefeituras(true);
      const response = await api.get(API_CONFIG.ENDPOINTS.PREFEITURA.CONSULTAR_PREFEITURA);
      
      if (response.data.status) {
        setPrefeituras(response.data.prefeituras || []);
      } else {
        message.error(response.data.message || 'Erro ao carregar prefeituras');
      }
    } catch (error) {
      //console.error('Erro ao carregar prefeituras:', error);
      if (error.response?.status === 401) {
        message.error('Sessão expirada. Por favor, faça login novamente.');
      } else {
        message.error('Erro ao carregar prefeituras');
      }
    } finally {
      setLoadingPrefeituras(false);
    }
  };

  const carregarSecretarias = async (prefeituraId) => {
    try {
      setLoadingSecretarias(true);
      const response = await api.get(API_CONFIG.ENDPOINTS.PREFEITURA.ORGAOS_DA_PREFEITURAS(prefeituraId));
      
      if (response.data.status) {
        // Acessando os órgãos dentro do objeto prefeitura
        setSecretarias(response.data.prefeitura?.orgaos || []);
      } else {
        message.error(response.data.message || 'Erro ao carregar secretarias');
      }
    } catch (error) {
      //console.error('Erro ao carregar secretarias:', error);
      if (error.response?.status === 401) {
        message.error('Sessão expirada. Por favor, faça login novamente.');
      } else {
        message.error('Erro ao carregar secretarias');
      }
    } finally {
      setLoadingSecretarias(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    if (name === 'placa') {
      formattedValue = formatPlaca(value);
    } else if (name === 'chassi') {
      formattedValue = formatChassi(value);
    }

    setForm(prevForm => ({ ...prevForm, [name]: formattedValue }));
    setTouched(prevTouched => ({ ...prevTouched, [name]: true }));
  };

  const validate = () => {
    const newErrors = {};
    if (!form.descricao?.trim()) newErrors.descricao = 'Campo obrigatório';
    if (!form.prefeitura?.trim()) newErrors.prefeitura = 'Campo obrigatório';
    if (!form.secretaria?.trim()) newErrors.secretaria = 'Campo obrigatório';
    
    if (!form.placa?.trim()) {
      newErrors.placa = 'Campo obrigatório';
    } else if (!(/^([A-Z]{3}[0-9]{4}|[A-Z]{3}[0-9]{1}[A-Z]{1}[0-9]{2})$/.test(form.placa.replace('-', '')))) {
      newErrors.placa = 'Formato de placa inválido (Ex: ABC-1234 ou ABC1D23)';
    }

    if (!form.chassi?.trim()) {
      newErrors.chassi = 'Campo obrigatório';
    } else if (form.chassi.length < 17) {
      newErrors.chassi = 'Chassi deve ter 17 caracteres';
    }

    if (!form.tipo?.trim()) newErrors.tipo = 'Campo obrigatório';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    const validationErrors = validate();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      try {
        setLoading(true);
        
        // Preparar payload no formato da API
        const payload = {
          VEI_DESCRICAO: form.descricao?.trim(),
          VEI_PREF: form.prefeitura,
          VEI_SEC: form.secretaria,
          VEI_PLACA: form.placa?.replace('-', ''),
          VEI_CHASSI: form.chassi?.trim(),
          VEI_TIPO: form.tipo,
          VEI_ORG_ID: form.secretaria
        };

        const response = await api.post(API_CONFIG.ENDPOINTS.VEICULOS.CADASTRAR, payload);

        if (response.data.status) {
          message.success(response.data.message || 'Veículo cadastrado com sucesso!');
          setForm(initialState);
          setTouched({});
          setSubmitted(false);
        } else {
          message.error(response.data.message || 'Erro ao cadastrar veículo');
        }
      } catch (error) {
        //console.error('Erro ao cadastrar veículo:', error);
        if (error.response?.status === 401) {
          message.error('Sessão expirada. Por favor, faça login novamente.');
        } else if (error.response?.status === 422) {
          message.error(error.response.data.message || 'Dados inválidos. Verifique os campos preenchidos.');
        } else {
          message.error(error.response?.data?.message || 'Erro ao cadastrar veículo');
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const showError = (field) => {
    return (submitted || touched[field]) && errors[field];
  };

  return (
    <div className={`home-container ${isMenuExpanded ? 'menu-expanded' : ''}`}>
      <div className="modal-wrapper">
        <h1 className="modal-title">Cadastrar Veículo</h1>
        <div className="modal-content">
          <form className="form-veiculo" onSubmit={handleSubmit} autoComplete="off">
            <div className="form-row">
              <div className="form-group full-width">
                <label className="form-label">Descrição do Veículo <span className="asterisco-obrigatorio">*</span></label>
                <input
                  type="text"
                  name="descricao"
                  placeholder="Descrição do Veículo"
                  value={form.descricao || ''}
                  onChange={handleChange}
                  className={showError('descricao') ? 'erro' : ''}
                />
                {showError('descricao') && <span className="msg-erro">{errors.descricao}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Prefeitura <span className="asterisco-obrigatorio">*</span></label>
                <select
                  name="prefeitura"
                  value={form.prefeitura || ''}
                  onChange={handleChange}
                  className={showError('prefeitura') ? 'erro' : ''}
                  disabled={loadingPrefeituras}
                >
                  <option value="">Selecione a Prefeitura</option>
                  {prefeituras.map(prefeitura => (
                    <option key={prefeitura.PRE_ID} value={prefeitura.PRE_ID}>
                      {prefeitura.PRE_NOME}
                    </option>
                  ))}
                </select>
                {showError('prefeitura') && <span className="msg-erro">{errors.prefeitura}</span>}
                {loadingPrefeituras && <span className="loading-text">Carregando prefeituras...</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Secretaria <span className="asterisco-obrigatorio">*</span></label>
                <select
                  name="secretaria"
                  value={form.secretaria || ''}
                  onChange={handleChange}
                  className={showError('secretaria') ? 'erro' : ''}
                  disabled={loadingSecretarias || !form.prefeitura}
                >
                  <option value="">Selecione a Secretaria</option>
                  {secretarias.map(secretaria => (
                    <option key={secretaria.ORG_ID} value={secretaria.ORG_ID}>
                      {secretaria.ORG_DESCRICAO}
                    </option>
                  ))}
                </select>
                {showError('secretaria') && <span className="msg-erro">{errors.secretaria}</span>}
                {loadingSecretarias && <span className="loading-text">Carregando secretarias...</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Placa <span className="asterisco-obrigatorio">*</span></label>
                <input
                  type="text"
                  name="placa"
                  placeholder="Ex: ABC-1234 ou ABC1D23"
                  value={form.placa || ''}
                  onChange={handleChange}
                  className={showError('placa') ? 'erro' : ''}
                  maxLength="8"
                />
                {showError('placa') && <span className="msg-erro">{errors.placa}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Chassi <span className="asterisco-obrigatorio">*</span></label>
                <input
                  type="text"
                  name="chassi"
                  placeholder="Ex: 9BWZZZ52ZPC******"
                  value={form.chassi || ''}
                  onChange={handleChange}
                  className={showError('chassi') ? 'erro' : ''}
                  maxLength="17"
                />
                {showError('chassi') && <span className="msg-erro">{errors.chassi}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Tipo <span className="asterisco-obrigatorio">*</span></label>
                <select
                  name="tipo"
                  value={form.tipo || ''}
                  onChange={handleChange}
                  className={showError('tipo') ? 'erro' : ''}
                >
                  <option value="">Selecione o Tipo</option>
                  {tiposVeiculo.map(tipo => (
                    <option key={tipo.id} value={tipo.id}>
                      {tipo.nome}
                    </option>
                  ))}
                </select>
                {showError('tipo') && <span className="msg-erro">{errors.tipo}</span>}
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="cadastro-submit-button"
                disabled={loading}
              >
                {loading ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
} 